import { authService } from './authService';

const API_BASE_URL = process.env.REACT_APP_SERVER_URL || 'http://localhost:3000';

/**
 * Commission Service - API calls for sales agent commission management
 */
export const commissionService = {
    /**
     * Assign sales agent to an order
     */
    async assignSalesAgent(orderId, salesAgentId, commissionRate = null) {
        try {
            const response = await fetch(`${API_BASE_URL}/commissions/assign`, {
                method: 'POST',
                headers: {
                    ...authService.getAuthHeaders(),
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    order_id: orderId,
                    sales_agent_id: salesAgentId,
                    commission_rate: commissionRate
                })
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Failed to assign sales agent');
            return data;
        } catch (error) {
            console.error('Error assigning sales agent:', error);
            throw error;
        }
    },

    /**
     * Remove sales agent from an order
     */
    async removeSalesAgent(orderId) {
        try {
            const response = await fetch(`${API_BASE_URL}/commissions/assign/${orderId}`, {
                method: 'DELETE',
                headers: authService.getAuthHeaders()
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Failed to remove sales agent');
            return data;
        } catch (error) {
            console.error('Error removing sales agent:', error);
            throw error;
        }
    },

    /**
     * Calculate commission for a single order
     */
    async calculateOrderCommission(orderId) {
        try {
            const response = await fetch(`${API_BASE_URL}/commissions/calculate/${orderId}`, {
                method: 'POST',
                headers: authService.getAuthHeaders()
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Failed to calculate commission');
            return data;
        } catch (error) {
            console.error('Error calculating commission:', error);
            throw error;
        }
    },

    /**
     * Calculate commissions for all orders in a period
     */
    async calculatePeriodCommissions(startDate, endDate) {
        try {
            const response = await fetch(`${API_BASE_URL}/commissions/calculate-period`, {
                method: 'POST',
                headers: {
                    ...authService.getAuthHeaders(),
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ start_date: startDate, end_date: endDate })
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Failed to calculate period commissions');
            return data;
        } catch (error) {
            console.error('Error calculating period commissions:', error);
            throw error;
        }
    },

    /**
     * Get monthly commission report
     */
    async getMonthlyReport(year, month) {
        try {
            const params = new URLSearchParams();
            if (year) params.append('year', year);
            if (month) params.append('month', month);

            const response = await fetch(`${API_BASE_URL}/commissions/report/monthly?${params}`, {
                method: 'GET',
                headers: authService.getAuthHeaders()
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Failed to get monthly report');
            return data;
        } catch (error) {
            console.error('Error getting monthly report:', error);
            throw error;
        }
    },

    /**
     * Get list of sales agents with stats
     */
    async getSalesAgents() {
        try {
            const response = await fetch(`${API_BASE_URL}/commissions/agents`, {
                method: 'GET',
                headers: authService.getAuthHeaders()
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Failed to get sales agents');
            return data;
        } catch (error) {
            console.error('Error getting sales agents:', error);
            throw error;
        }
    },

    /**
     * Get orders for a specific sales agent
     */
    async getAgentOrders(agentId, filters = {}) {
        try {
            const params = new URLSearchParams();
            if (filters.status) params.append('status', filters.status);
            if (filters.commission_status) params.append('commission_status', filters.commission_status);
            if (filters.start_date) params.append('start_date', filters.start_date);
            if (filters.end_date) params.append('end_date', filters.end_date);
            if (filters.page) params.append('page', filters.page);
            if (filters.limit) params.append('limit', filters.limit);

            const response = await fetch(`${API_BASE_URL}/commissions/agent/${agentId}/orders?${params}`, {
                method: 'GET',
                headers: authService.getAuthHeaders()
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Failed to get agent orders');
            return data;
        } catch (error) {
            console.error('Error getting agent orders:', error);
            throw error;
        }
    },

    /**
     * Mark commissions as paid for an agent
     */
    async markCommissionsAsPaid(salesAgentId, year, month) {
        try {
            const response = await fetch(`${API_BASE_URL}/commissions/mark-paid`, {
                method: 'POST',
                headers: {
                    ...authService.getAuthHeaders(),
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ sales_agent_id: salesAgentId, year, month })
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Failed to mark commissions as paid');
            return data;
        } catch (error) {
            console.error('Error marking commissions as paid:', error);
            throw error;
        }
    },

    /**
     * Get commission configuration
     */
    async getConfig() {
        try {
            const response = await fetch(`${API_BASE_URL}/commissions/config`, {
                method: 'GET',
                headers: authService.getAuthHeaders()
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Failed to get commission config');
            return data;
        } catch (error) {
            console.error('Error getting commission config:', error);
            throw error;
        }
    },

    /**
     * Update commission configuration
     */
    async updateConfig(configData) {
        try {
            const response = await fetch(`${API_BASE_URL}/commissions/config`, {
                method: 'PUT',
                headers: {
                    ...authService.getAuthHeaders(),
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(configData)
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Failed to update commission config');
            return data;
        } catch (error) {
            console.error('Error updating commission config:', error);
            throw error;
        }
    },

    /**
     * Get default commission configuration
     */
    async getDefaultConfig() {
        try {
            const response = await fetch(`${API_BASE_URL}/commissions/config/defaults`, {
                method: 'GET',
                headers: authService.getAuthHeaders()
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.error || 'Failed to get default config');
            return data;
        } catch (error) {
            console.error('Error getting default config:', error);
            throw error;
        }
    }
};
